// ============================================================================
// Executive Dashboard — personalized widget grid (2026-06-11).
//
// What changed vs the fixed-grid original:
//   * Every widget is registered in widget-registry.ts (stable widget_key,
//     role gate, grid size, default visibility, data domains).
//   * Per-user show/hide + ordering persists in user_dashboard_prefs (DB,
//     never browser storage — works across devices). No prefs row → the
//     default layout, which is exactly the pre-personalization dashboard.
//   * "Customize" opens the tailor panel (drawer with checkboxes + drag).
//   * "Edit layout" toggles drag-to-reorder directly on the grid
//     (@dnd-kit/sortable; touch sensors + per-card arrows for mobile).
//   * Hidden widgets don't fetch: useDashboardData receives the union of
//     visible widgets' data domains and skips the rest; self-fetching
//     widgets (finance/HR/invoicing/hiring/incentive) simply don't mount.
//   * Role gates still rule: the registry filters both the catalog and the
//     grid — prefs JSON can never surface a widget to a forbidden role.
//
// Layout: one grid, 1 col mobile / 2 md / 6 xl. Widget size → span:
//   small  = 1/2 row at md, 1/3 row at xl   (the six core widgets)
//   medium = full row at md, 1/2 row at xl  (inboxes, margins, trends)
//   full   = full row always                (KPI rows, kanban-ish tiles)
// ============================================================================

import { useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  Check,
  GripVertical,
  LayoutGrid,
  RefreshCw,
  Scale,
  Settings2,
  SlidersHorizontal,
} from 'lucide-react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDashboardData, type DashboardNeeds } from './use-dashboard-data';
import { useDashboardPrefs } from './use-dashboard-prefs';
import { TailorPanel } from './tailor-panel';
import {
  WIDGET_BY_KEY,
  type DataDomain,
  type LayoutEntry,
  type WidgetSize,
} from './widget-registry';
import { FacilityHealthWidget } from './facility-health-widget';
import { RevenueWidget } from './revenue-widget';
import { RevenueTrendWidget } from './revenue-trend-widget';
import { AccountHealthWidget } from './account-health-widget';
import { PipelineWidget } from './pipeline-widget';
import { AlertsWidget } from './alerts-widget';
import { GeographicWidget } from './geographic-widget';
import { PortfolioKpiWidget } from './portfolio-kpi-widget';
import { MarginDistributionWidget } from './margin-distribution-widget';
import { MarginExtremesWidget } from './margin-extremes-widget';
import { FinanceWidget } from './widgets/finance-widget';
import { InvoicingWidget } from './widgets/invoicing-widget';
import { KickersWidget } from './widgets/kickers-widget';
import { HrWidget } from './widgets/hr-widget';
import { HiringKpisWidget } from './widgets/hiring-kpis-widget';
import { NewAccountsWidget } from './widgets/new-accounts-widget';
import { MarketPlWidget } from './widgets/market-pl/market-pl-widget';
import { useMarketPlViewer } from './widgets/market-pl/use-market-pl';
import { Button, EmptyState, ErrorState, PageHeader } from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { hasFinanceCapability } from '../../shared/role-gates';
import { IncentiveQueueWidget } from '../incentive';
import {
  FINANCE_TAB_META,
  FinanceView,
  canSeeFinanceView,
  type FinanceTab,
} from '../finance';

const SPAN_CLASSES: Record<WidgetSize, string> = {
  small: 'md:col-span-1 xl:col-span-2',
  medium: 'md:col-span-2 xl:col-span-3',
  full: 'md:col-span-2 xl:col-span-6',
};

// CW-Q: the widget grid is now ONE TAB of the Dashboard section rather than the
// whole of it. Nothing below this line changed — the grid, the prefs, the role
// gates and the edit mode are exactly as they were. The tab shell is the new
// default export at the bottom of this file.
function WidgetDashboard() {
  const { profile, employee } = useAuth();
  const role = profile?.role ?? null;
  const myEid = employee?.employee_id ?? null;

  const prefs = useDashboardPrefs(role);
  const layout = prefs.layout;
  const [panelOpen, setPanelOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Visible widgets in user order. resolveLayout already enforced role gates
  // and dropped unknown keys; the WIDGET_BY_KEY check is belt-and-braces.
  const visible = useMemo(
    () =>
      layout.filter(
        (e) => e.visible && WIDGET_BY_KEY.has(e.widget_key),
      ),
    [layout],
  );

  // Union of data domains the visible widgets consume → hidden widgets'
  // queries are skipped entirely in useDashboardData.
  const needs = useMemo<DashboardNeeds>(() => {
    const domains = new Set<DataDomain>();
    for (const e of visible) {
      const def = WIDGET_BY_KEY.get(e.widget_key);
      def?.domains.forEach((d) => domains.add(d));
    }
    return {
      facilities: domains.has('facilities'),
      accounts: domains.has('accounts'),
      opportunities: domains.has('opportunities'),
      feedback: domains.has('feedback'),
      assignments: domains.has('assignments'),
      hiring: domains.has('hiring'),
    };
  }, [visible]);

  // Data fetch waits for prefs to settle so the first paint doesn't fire an
  // all-domains fetch for widgets the user has hidden.
  const dash = useDashboardData(needs, prefs.ready);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Reorder within the VISIBLE sequence, then write back into the full
  // layout so hidden widgets keep their slots (and their tailor-panel order).
  const applyVisibleOrder = (newVisible: LayoutEntry[]) => {
    const full = [...layout];
    const visIdx = full
      .map((e, i) => (e.visible && WIDGET_BY_KEY.has(e.widget_key) ? i : -1))
      .filter((i) => i >= 0);
    visIdx.forEach((fullIndex, j) => {
      full[fullIndex] = newVisible[j];
    });
    prefs.setLayout(full);
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = visible.findIndex((e) => e.widget_key === active.id);
    const to = visible.findIndex((e) => e.widget_key === over.id);
    if (from < 0 || to < 0) return;
    applyVisibleOrder(arrayMove(visible, from, to));
  };

  const moveVisible = (key: string, delta: -1 | 1) => {
    const from = visible.findIndex((e) => e.widget_key === key);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= visible.length) return;
    applyVisibleOrder(arrayMove(visible, from, to));
  };

  // Widget renderers, keyed by widget_key. Self-fetching widgets only mount
  // when visible, so hiding them drops their queries too.
  const renderWidget = (key: string): ReactNode => {
    switch (key) {
      case 'finance_inbox':
        return <FinanceWidget enabled myEid={myEid} />;
      case 'hr_inbox':
        return <HrWidget enabled myEid={myEid} />;
      case 'invoicing':
        return <InvoicingWidget enabled />;
      case 'hiring_kpis':
        return <HiringKpisWidget enabled />;
      case 'kicker_queue':
        return <KickersWidget enabled />;
      case 'new_accounts':
        return <NewAccountsWidget enabled />;
      case 'incentive_queue':
        return <IncentiveQueueWidget />;
      case 'portfolio_kpis':
        return (
          <PortfolioKpiWidget
            data={dash.portfolioSummary}
            loading={dash.loading}
          />
        );
      case 'margin_distribution':
        return (
          <MarginDistributionWidget
            data={dash.marginDistribution}
            loading={dash.loading}
          />
        );
      case 'margin_extremes':
        return (
          <MarginExtremesWidget
            data={dash.marginExtremes}
            loading={dash.loading}
            dataQualityExcludedCount={
              dash.portfolioSummary?.margin_data_quality_excluded_count
            }
          />
        );
      case 'facility_health':
        return (
          <FacilityHealthWidget
            data={dash.facilityHealth}
            loading={dash.loading}
          />
        );
      case 'revenue':
        return <RevenueWidget data={dash.revenue} loading={dash.loading} />;
      case 'revenue_trend':
        return (
          <RevenueTrendWidget data={dash.revenue} loading={dash.loading} />
        );
      case 'alerts':
        return <AlertsWidget data={dash.alerts} loading={dash.loading} />;
      case 'account_health':
        return (
          <AccountHealthWidget
            data={dash.accountHealth}
            loading={dash.loading}
          />
        );
      case 'pipeline':
        return <PipelineWidget data={dash.pipeline} loading={dash.loading} />;
      case 'geographic':
        return (
          <GeographicWidget data={dash.geographic} loading={dash.loading} />
        );
      default:
        return null;
    }
  };

  const grid = (
    <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
      {visible.map((entry, i) => {
        const def = WIDGET_BY_KEY.get(entry.widget_key);
        if (!def) return null;
        const cell = renderWidget(entry.widget_key);
        if (cell === null) return null;
        return editMode ? (
          <SortableCell
            key={entry.widget_key}
            id={entry.widget_key}
            spanClass={SPAN_CLASSES[def.size]}
            name={def.name}
            isFirst={i === 0}
            isLast={i === visible.length - 1}
            onMoveUp={() => moveVisible(entry.widget_key, -1)}
            onMoveDown={() => moveVisible(entry.widget_key, 1)}
          >
            {cell}
          </SortableCell>
        ) : (
          <div key={entry.widget_key} className={SPAN_CLASSES[def.size]}>
            {cell}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl p-4 md:p-6">
      <PageHeader
        eyebrow="Your widgets · scan and drill"
        title="My Dashboard"
        meta={
          <span className="tabular-nums">
            {dash.lastUpdated
              ? `Refreshed ${formatTime(dash.lastUpdated)}`
              : dash.loading
                ? 'Loading…'
                : '—'}
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={dash.refresh}
              disabled={dash.loading}
              icon={<RefreshCw className="h-3.5 w-3.5" />}
            >
              Refresh
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPanelOpen(true)}
              icon={<Settings2 className="h-3.5 w-3.5" />}
            >
              Customize
            </Button>
            <Button
              variant={editMode ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setEditMode((v) => !v)}
              icon={
                editMode ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <SlidersHorizontal className="h-3.5 w-3.5" />
                )
              }
            >
              {editMode ? 'Done' : 'Edit layout'}
            </Button>
          </div>
        }
      />

      {dash.error && (
        <div className="mt-4">
          <ErrorState body={dash.error} onRetry={dash.refresh} />
        </div>
      )}

      {prefs.saveError && !panelOpen && (
        <p className="mt-3 rounded border border-st-danger-border bg-st-danger-bg px-3 py-2 text-xs text-st-danger-fg">
          Couldn't save your layout: {prefs.saveError}
        </p>
      )}

      {editMode && (
        <p className="mt-3 rounded border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-xs text-tcs-text-secondary">
          Edit layout — drag a card by its handle (or use the arrows) to
          reorder. Changes save automatically; tap Done when finished.
        </p>
      )}

      {visible.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            title="All widgets are hidden"
            body="Open Customize to choose what appears on your dashboard."
            cta={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPanelOpen(true)}
              >
                Customize
              </Button>
            }
          />
        </div>
      ) : editMode ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={visible.map((e) => e.widget_key)}
            strategy={rectSortingStrategy}
          >
            {grid}
          </SortableContext>
        </DndContext>
      ) : (
        grid
      )}

      <TailorPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        layout={layout}
        onChange={prefs.setLayout}
        onReset={prefs.reset}
        hasCustom={prefs.hasCustom}
        saveError={prefs.saveError}
      />
    </div>
  );
}

// ----- Edit-mode sortable cell ------------------------------------------------
// Wraps a widget in a dnd-kit sortable with a visible drag handle + up/down
// arrows (the arrows are the mobile/keyboard fallback). Widget content is
// pointer-inert in edit mode so links/buttons inside cards can't hijack a
// drag gesture.

interface SortableCellProps {
  id: string;
  spanClass: string;
  name: string;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  children: ReactNode;
}

function SortableCell({
  id,
  spanClass,
  name,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  children,
}: SortableCellProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={`relative rounded-xl ${spanClass} ${
        isDragging ? 'z-20 opacity-90 shadow-xl ring-2 ring-tcs-border-input' : 'ring-1 ring-dashed ring-tcs-border-strong'
      }`}
    >
      <div className="absolute right-2 top-2 z-10 flex items-center gap-1 rounded-lg border border-tcs-border bg-tcs-overlay/95 p-0.5 shadow-sm">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={isFirst}
          className="rounded p-1 text-tcs-text-muted hover:text-tcs-text-secondary disabled:opacity-30"
          aria-label={`Move ${name} earlier`}
        >
          <ArrowUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={isLast}
          className="rounded p-1 text-tcs-text-muted hover:text-tcs-text-secondary disabled:opacity-30"
          aria-label={`Move ${name} later`}
        >
          <ArrowDown className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className="cursor-grab touch-none rounded p-1 text-tcs-text-muted hover:text-tcs-text-secondary active:cursor-grabbing"
          aria-label={`Drag to reorder ${name}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </div>
      <div className="pointer-events-none select-none">{children}</div>
    </div>
  );
}

// 24h or local-format time, no seconds. Keeping it short so the header
// doesn't wrap on narrow viewports.
function formatTime(d: Date): string {
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ============================================================================
// CW-Q — the Dashboard tab shell.
//
// Jose's finance view (Bryan signed off Page 1 and Page 2 on 2026-08-12) lands
// INSIDE Dashboard as a tab row at the top, mirroring the My Work pattern, with
// "Executive Overview" first and click-through to the Portfolio.
//
// Two deliberate choices:
//
//   * `?tab=` drives the active view, exactly as My Work does, so a deep link
//     into the Executive Overview survives a page reload and can be pasted into
//     a board pack.
//   * For a viewer WITHOUT the finance capability there is only one tab, so no
//     tab row renders and the Dashboard is byte-for-byte what it was before.
//     An AM does not get an empty finance page, and does not get a chrome change
//     they cannot use. Role gating is on the capability (primary role OR the
//     `finance` extra-role hat), never on role equality — Antonela is HR primary
//     with a finance hat and that hat IS the finance role for her.
// ============================================================================

type DashboardTab = FinanceTab | 'widgets' | 'market-pl';

function parseTab(raw: string | null, allowFinance: boolean, allowMarketPl: boolean): DashboardTab {
  if (raw === 'widgets') return 'widgets';
  if (allowMarketPl && raw === 'market-pl') return 'market-pl';
  if (allowFinance && (raw === 'overview' || raw === 'portfolio')) return raw;
  // Default: the finance-capable viewer lands on the Executive Overview, which
  // is what "first tab" means. Everyone else lands where they always have.
  return allowFinance ? 'overview' : 'widgets';
}

export default function Dashboard() {
  const { profile, employee } = useAuth();
  const allowFinance = canSeeFinanceView(
    profile?.role ?? null,
    profile?.extra_roles ?? null,
  );
  // Market P&L is its own tab (Ben 2026-11-03). Who sees it is
  // is_market_pl_viewer() — finance, executives (role or Exec employee type),
  // market leads — asked once; RLS on its tables is the hard boundary. While
  // the answer is pending a ?tab=market-pl link is held, not bounced.
  const marketPlViewer = useMarketPlViewer(!!profile);
  const [params, setParams] = useSearchParams();
  const active = parseTab(params.get('tab'), allowFinance, marketPlViewer !== false);

  const select = (tab: DashboardTab) => {
    const next = new URLSearchParams(params);
    next.set('tab', tab);
    setParams(next, { replace: true });
  };

  // One tab is not a tab row. Render the grid exactly as before.
  if (!allowFinance && marketPlViewer !== true && active !== 'market-pl') return <WidgetDashboard />;

  const tabs: { id: DashboardTab; label: string; icon: typeof LayoutGrid }[] = [
    ...(allowFinance ? FINANCE_TAB_META : []),
    ...(marketPlViewer === true ? [{ id: 'market-pl' as const, label: 'Market P&L', icon: Scale }] : []),
    { id: 'widgets', label: 'My Dashboard', icon: LayoutGrid },
  ];

  return (
    <div className="flex min-h-full flex-col">
      <div className="border-b border-tcs-border bg-tcs-bg px-4 pt-5 md:px-6">
        <div className="font-mono text-[10px] uppercase tracking-wide text-tcs-text-muted">
          Dashboard · the TCS portfolio
        </div>
        <h1 className="mt-1 text-xl font-semibold text-tcs-text">Dashboard</h1>
        <div
          role="tablist"
          className="mt-4 flex gap-1 overflow-x-auto border-b border-tcs-border"
        >
          {tabs.map((t) => {
            const isActive = t.id === active;
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                role="tab"
                type="button"
                aria-selected={isActive}
                onClick={() => select(t.id)}
                className={[
                  '-mb-px inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'border-tcs-text font-medium text-tcs-text'
                    : 'border-transparent text-tcs-text-secondary hover:text-tcs-text',
                ].join(' ')}
              >
                <Icon className="h-4 w-4" />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        {active === 'widgets' ? (
          <WidgetDashboard />
        ) : active === 'market-pl' ? (
          <div className="mx-auto w-full max-w-7xl p-4 md:p-6">
            {marketPlViewer === true ? (
              <MarketPlWidget canEdit={hasFinanceCapability(profile)} myEid={employee?.employee_id ?? null} />
            ) : (
              <p className="text-sm text-tcs-text-muted">Loading…</p>
            )}
          </div>
        ) : (
          <FinanceView
            tab={active}
            onTabChange={(t) => select(t)}
          />
        )}
      </div>
    </div>
  );
}
